import React, { useState } from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import { getAllScenes } from '../../utils/novelUtils';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import './StaticSceneNextScenes.css';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = { scene_id: '', scene_type: 'static', priority: 0, conditionals: [] };

const StaticSceneNextScenes = ({
  novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction = false
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, 'static');
  const [sceneSearch, setSceneSearch] = useState('');

  const getSceneHref = (targetSceneId, sceneType) => {
    const basePath = isProduction ? 'production_change' : 'building';
    const sceneTypePath = sceneType === 'interactive' ? 'interactive_scene' : sceneType === 'open_world' ? 'open_world_scene' : 'static_scene';
    return `/${basePath}/${novelId}/${sceneTypePath}/${targetSceneId}`;
  };

  const allScenes = getAllScenes(novel);
  const getAvailableScenes = (searchTerm = '') =>
    allScenes.filter(s => s.type === formData.scene_type && (s.id === formData.scene_id || s.name.toLowerCase().includes(searchTerm.toLowerCase()))).sort((a, b) => a.name.localeCompare(b.name));
  const getSceneName = (sid, sceneType) => allScenes.find(s => s.id === sid && s.type === sceneType)?.name || 'Неизвестная сцена';

  const handleEditNextSceneClick = (nextScene, index) => {
    openEdit({
      scene_id: nextScene.scene_id || '',
      scene_type: nextScene.scene_type || 'static',
      priority: nextScene.priority || 0,
      conditionals: nextScene.conditionals || []
    }, index);
  };

  const handleAddNextScene = () => { openAdd(); setSceneSearch(''); };

  const handleSaveNextScene = async () => {
    if (loading) return;
    setError(null);
    const updatedNextScenes = [...(scene.next_scenes || [])];
    if (editingIndex !== null) {
      updatedNextScenes[editingIndex] = formData;
    } else {
      updatedNextScenes.push(formData);
    }
    const success = await saveScene({ ...scene, next_scenes: updatedNextScenes }, 'Следующая сцена успешно сохранена!');
    if (success) close();
  };

  const handleDeleteNextScene = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту следующую сцену?')) return;
    setError(null);
    const updatedNextScenes = [...(scene.next_scenes || [])];
    updatedNextScenes.splice(index, 1);
    await saveScene({ ...scene, next_scenes: updatedNextScenes }, 'Следующая сцена успешно удалена!');
  };

  const handleCancelNextSceneForm = () => { close(); setSceneSearch(''); };

  return (
    <div className="ssn-wrap">
      <div className="ssn-header">
        <h3>Следующие сцены</h3>
        <button className="ssn-add-btn" onClick={handleAddNextScene} disabled={loading}>
          Добавить следующую сцену
        </button>
      </div>

      <div className="ssn-help">
        <p>Настройте, какие сцены могут следовать за этой статической сценой и при каких условиях</p>
      </div>

      {showForm && (
        <div className="ssn-overlay">
          <div className="ssn-modal">
            <h4>{editingIndex !== null ? 'Редактировать следующую сцену' : 'Добавить следующую сцену'}</h4>

            <div className="ssn-form-group">
              <label>Тип сцены:</label>
              <CustomSelect
                value={formData.scene_type}
                onChange={(e) => setFormData({ ...formData, scene_type: e.target.value, scene_id: '' })}
                className="ssn-input" disabled={loading}
              >
                <option value="static">Статическая</option>
                <option value="interactive">Интерактивная</option>
                <option value="open_world">Открытый мир</option>
              </CustomSelect>
            </div>

            <div className="ssn-form-group">
              <label>Выбор сцены:</label>
              <input
                type="text" placeholder="Поиск сцен..."
                value={sceneSearch} onChange={(e) => setSceneSearch(e.target.value)}
                className="ssn-input ssn-search-input"
              />
              <CustomSelect
                value={formData.scene_id}
                onChange={(e) => setFormData({ ...formData, scene_id: e.target.value })}
                className="ssn-input" disabled={loading}
              >
                <option value="">Нет следующей сцены (конец истории)</option>
                {getAvailableScenes(sceneSearch).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="ssn-form-group">
              <label>Приоритет:</label>
              <input
                type="number" value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="ssn-input" disabled={loading} min="0"
              />
              <small className="ssn-form-help">Сцены с более высоким приоритетом выбираются первыми, когда выполняется несколько условий</small>
            </div>

            <div className="ssn-form-group">
              <label>Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []}
                scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)}
                value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })}
                disabled={loading}
              />
            </div>

            <div className="ssn-form-actions">
              <button type="button" onClick={handleSaveNextScene} disabled={loading} className="ssn-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={handleCancelNextSceneForm} disabled={loading} className="ssn-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ssn-list">
        {scene?.next_scenes && scene.next_scenes.length > 0 ? (
          <div className="ssn-grid">
            {scene.next_scenes.map((nextScene, index) => (
              <div key={index} className="ssn-card">
                <div className="ssn-card-head">
                  <h4 className="ssn-card-name">
                    {nextScene.scene_id ? (
                      <a href={getSceneHref(nextScene.scene_id, nextScene.scene_type)} className="ssn-card-link">
                        {getSceneName(nextScene.scene_id, nextScene.scene_type)}
                      </a>
                    ) : 'Нет следующей сцены (конец истории)'}
                  </h4>
                </div>
                <div className="ssn-card-actions">
                  {nextScene.scene_id && (
                    <a href={getSceneHref(nextScene.scene_id, nextScene.scene_type)} className="ssn-btn-link">
                      Перейти к сцене
                    </a>
                  )}
                  <button className="ssn-btn-edit" onClick={() => handleEditNextSceneClick(nextScene, index)} disabled={loading}>
                    Редактировать следующую сцену
                  </button>
                  <button className="ssn-btn-delete" onClick={() => handleDeleteNextScene(index)} disabled={loading}>
                    Удалить
                  </button>
                </div>
                <div className="ssn-card-body">
                  <div className="ssn-details">
                    <p><strong>Тип сцены:</strong> {nextScene.scene_type === 'interactive' ? 'Интерактивная' : 'Статическая'}</p>
                    <p><strong>Приоритет:</strong> {nextScene.priority}</p>
                    {nextScene.conditionals && nextScene.conditionals.length > 0 && (
                      <p><strong>Условия:</strong> {nextScene.conditionals.length}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ssn-empty">
            <p>Для этой сцены пока не настроены следующие сцены.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaticSceneNextScenes;
